import React, { useMemo, forwardRef, useImperativeHandle, useRef } from 'react'
import ReactECharts from 'echarts-for-react'
import * as echarts from 'echarts'
import { ChartContainer } from './CardisValuationChart.styles'
import {
  ValuationChartData,
  FinalValuationData,
} from './CardisEstimationTechnique.types'

export interface CardisValuationChartProps {
  data: ValuationChartData[]
  currency?: string
  height?: string
  finalValuation?: FinalValuationData
}

// Cardis brand colors
const CARDIS_COLORS = {
  primary: '#003057',
  gold: '#c9a961',
}

export interface CardisValuationChartRef {
  exportChart: () => string | null
}

const formatCurrency = (value: number, currency: string): string => {
  return `${currency} ${value.toLocaleString('de-CH')}`
}

// Helper to wrap text into multiple lines
const wrapLabel = (text: string, maxLineLength: number = 14): string => {
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    if (currentLine && (currentLine + ' ' + word).length > maxLineLength) {
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = currentLine ? currentLine + ' ' + word : word
    }
  }
  if (currentLine) lines.push(currentLine)

  // Truncate to 2 lines max, with ellipsis if needed
  return lines
    .slice(0, 2)
    .map((l, i) => {
      if (i === 1 && lines.length > 2) {
        return l.length > maxLineLength - 3
          ? l.slice(0, maxLineLength - 3) + '...'
          : l + '...'
      }
      return l.length > maxLineLength
        ? l.slice(0, maxLineLength - 3) + '...'
        : l
    })
    .join('\n')
}

export const CardisValuationChart = forwardRef<
  CardisValuationChartRef,
  CardisValuationChartProps
>(({ data, currency = 'CHF', height = '280px', finalValuation }, ref) => {
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  const option = useMemo(() => {
    // Include final valuation values in the range calculation
    const finalValues = finalValuation?.value
      ? [
          finalValuation.value,
          finalValuation.range?.min,
          finalValuation.range?.max,
        ].filter((v): v is number => v !== undefined)
      : []

    // Calculate domain for better y-axis range
    const allValues = [
      ...data.flatMap((d) => [
        d.confidenceLow || d.value,
        d.value,
        d.confidenceHigh || d.value,
      ]),
      ...finalValues,
    ]
    const minValue = Math.min(...allValues)
    const maxValue = Math.max(...allValues)
    const padding = (maxValue - minValue) * 0.2

    // Grid config
    const gridLeft = 50
    const gridRight = finalValuation?.value ? 80 : 30
    const gridTop = 30
    const gridBottom = 60

    return {
      grid: {
        left: gridLeft,
        right: gridRight,
        top: gridTop,
        bottom: gridBottom,
        containLabel: false,
      },
      xAxis: {
        type: 'category',
        data: data.map((d) => d.name),
        axisLine: {
          lineStyle: {
            color: '#e0e0e0',
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          fontSize: 11,
          color: CARDIS_COLORS.primary,
          interval: 0,
          formatter: (value: string) => wrapLabel(value, 14),
          lineHeight: 14,
        },
      },
      yAxis: {
        type: 'value',
        min: Math.max(0, minValue - padding),
        max: maxValue + padding,
        axisLine: {
          show: true,
          lineStyle: {
            color: '#e0e0e0',
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          fontSize: 10,
          color: '#666',
          formatter: (value: number) => `${(value / 1000000).toFixed(2)}M`,
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: '#f0f0f0',
          },
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const item = data[params.dataIndex]
          let html = `<div style="padding: 4px;">
            <div style="font-weight: 600; margin-bottom: 8px; color: ${CARDIS_COLORS.primary};">${item.name}</div>
            <div style="color: ${item.color}; font-weight: 600; margin-bottom: 4px;">
              ${formatCurrency(item.value, currency)}
            </div>`

          if (item.confidenceLow && item.confidenceHigh) {
            html += `<div style="font-size: 12px; color: #666;">
              Range: ${formatCurrency(item.confidenceLow, currency)} - ${formatCurrency(item.confidenceHigh, currency)}
            </div>`
          }

          html += '</div>'
          return html
        },
        backgroundColor: '#fff',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        padding: 12,
        textStyle: {
          color: '#333',
        },
      },
      series: [
        // Final valuation reference lines (horizontal dashed lines)
        ...(finalValuation?.value
          ? [
              {
                type: 'custom',
                renderItem: (_params: any, api: any) => {
                  const chartWidth = api.getWidth()
                  const gridX = gridLeft
                  const gridWidth = chartWidth - gridLeft - gridRight

                  const children: any[] = []

                  // Helper to create a horizontal line with label
                  const createLine = (
                    yValue: number,
                    isDashed: boolean,
                    showLabel: boolean
                  ) => {
                    const yPixel = api.coord([0, yValue])[1]
                    const lineElements: any[] = [
                      {
                        type: 'line',
                        shape: {
                          x1: gridX,
                          y1: yPixel,
                          x2: gridX + gridWidth,
                          y2: yPixel,
                        },
                        style: {
                          stroke: CARDIS_COLORS.primary,
                          lineWidth: isDashed ? 1 : 2,
                          opacity: isDashed ? 0.5 : 0.8,
                          ...(isDashed ? { lineDash: [6, 4] } : {}),
                        },
                      },
                    ]

                    // Add label on the right side
                    if (showLabel || isDashed) {
                      lineElements.push({
                        type: 'text',
                        x: gridX + gridWidth + 5,
                        y: yPixel,
                        style: {
                          text: `${(yValue / 1000000).toFixed(2)}M`,
                          fontSize: 10,
                          fill: CARDIS_COLORS.primary,
                          fontWeight: showLabel ? 600 : 400,
                          textVerticalAlign: 'middle',
                        },
                      })
                    }

                    return lineElements
                  }

                  // Main value line (solid)
                  if (finalValuation.value) {
                    children.push(
                      ...createLine(finalValuation.value, false, true)
                    )
                  }

                  // Range min line (dashed)
                  if (finalValuation.range?.min) {
                    children.push(
                      ...createLine(finalValuation.range.min, true, false)
                    )
                  }

                  // Range max line (dashed)
                  if (finalValuation.range?.max) {
                    children.push(
                      ...createLine(finalValuation.range.max, true, false)
                    )
                  }

                  return {
                    type: 'group',
                    children,
                  }
                },
                data: [0],
                z: 0,
                silent: true,
              },
            ]
          : []),
        // Range lines (vertical bars with confidence intervals)
        {
          type: 'custom',
          renderItem: (params: any, api: any) => {
            const item = data[params.dataIndex]
            if (!item.confidenceLow || !item.confidenceHigh) return null

            const xValue = api.value(0)
            const yHigh = api.coord([xValue, item.confidenceHigh])
            const yLow = api.coord([xValue, item.confidenceLow])
            const x = api.coord([xValue, item.value])[0]

            const optimistText = formatCurrency(item.confidenceHigh, currency)
            const pessimistText = formatCurrency(item.confidenceLow, currency)

            const children = [
              // Vertical line (range bar)
              {
                type: 'line',
                shape: {
                  x1: x,
                  y1: yHigh[1],
                  x2: x,
                  y2: yLow[1],
                },
                style: {
                  stroke: item.color,
                  lineWidth: 6,
                  lineCap: 'round',
                  opacity: 0.3,
                },
              },
              // Optimist label (top)
              {
                type: 'text',
                x: x,
                y: yHigh[1] - 18,
                style: {
                  text: optimistText,
                  fontSize: 10,
                  fill: item.color,
                  textAlign: 'center',
                  fontWeight: 500,
                },
              },
              // Pessimist label (bottom)
              {
                type: 'text',
                x: x,
                y: yLow[1] + 12,
                style: {
                  text: pessimistText,
                  fontSize: 10,
                  fill: item.color,
                  textAlign: 'center',
                  fontWeight: 500,
                },
              },
            ]

            return {
              type: 'group',
              children,
            }
          },
          data: data.map((item, index) => [index, item.value]),
          z: 1,
        },
        // Value dots (scatter)
        {
          type: 'scatter',
          data: data.map((item, index) => ({
            value: [index, item.value],
            itemStyle: { color: item.color },
            label: {
              show: true,
              position: 'right',
              formatter: formatCurrency(item.value, currency),
              fontSize: 11,
              fontWeight: 600,
              color: item.color,
              offset: [10, 0],
            },
          })),
          symbolSize: 12,
          z: 2,
        },
      ],
    }
  }, [data, currency, finalValuation])

  return (
    <ChartContainer>
      <ReactECharts
        option={option}
        notMerge
        style={{ height, width: '100%' }}
        opts={{ renderer: 'svg' }}
        onChartReady={(chart) => {
          chartInstance.current = chart
        }}
      />
    </ChartContainer>
  )
})

CardisValuationChart.displayName = 'CardisValuationChart'
